<template>
  <div class="admin-subcategories">
    <div class="page-header">
      <div class="breadcrumbs">
         <router-link to="/admin/categories" class="breadcrumb-link">← {{ $t('legacy.Категории') }}</router-link>
        <span class="breadcrumb-separator">/</span>
        <span v-if="!parentSubcategory" class="breadcrumb-current">{{ categoryName }}</span>
        <template v-else>
          <span class="breadcrumb-link" @click="backToParent">{{ categoryName }}</span>
          <span class="breadcrumb-separator">/</span>
          <span class="breadcrumb-current">{{ parentSubcategory.name }}</span>
        </template>
      </div>
       <button @click="openCreateModal" class="btn-primary">
         + {{ $t('legacy.Создать') }} {{ parentSubcategory ? $t('Подподкатегорию') : $t('Подкатегорию') }}
      </button>
    </div>

    <div v-if="loading" class="loading-state">
       <p>{{ $t('common.loading') }}</p>
    </div>

    <div v-else class="subcategories-list">
      <div v-if="subcategories.length === 0" class="empty-state">
         <p>{{ $t('Нет') }} {{ parentSubcategory ? $t('подподкатегорий') : $t('подкатегорий') }}. {{ $t('Создайте первую!') }}</p>
      </div>

      <div v-for="subcategory in subcategories" :key="subcategory.id" class="subcategory-card">
        <div class="subcategory-cover" v-if="subcategory.cover_image">
           <img :src="subcategory.cover_image" :alt="$t('Обложка')" />
        </div>
        <div class="subcategory-info">
          <h3>{{ subcategory.name }}</h3>
           <p v-if="getEnglishName(subcategory)" class="english-name">
             {{ $t('admin.englishName') }}: {{ getEnglishName(subcategory) }}
           </p>
           <p>{{ $t('legacy.Порядок') }}: {{ subcategory.sort_order }}</p>
           <p>{{ $t('Элементы') }}: {{ subcategory.element_count }}</p>
          <p v-if="parseInt(subcategory.children_count) > 0" class="children-info">
             {{ $t('Подподкатегорий') }}: {{ subcategory.children_count }}
          </p>
          <p v-if="canAddElementsToSubcategory(subcategory)" class="can-add-elements">
             {{ $t('Можно добавлять элементы') }}
          </p>
          <p v-else class="cannot-add-elements">
             {{ $t('Есть дочерние, элементы добавлять нельзя') }}
          </p>
        </div>
        <div class="subcategory-actions">
          <button 
            v-if="!parentSubcategory" 
            @click="openChildren(subcategory)" 
            class="btn-children"
          >
             {{ $t('Подподкатегории') }} ({{ subcategory.children_count || 0 }})
          </button>
           <button @click="editSubcategory(subcategory)" class="btn-edit">{{ $t('common.edit') }}</button>
          <button 
            @click="deleteSubcategory(subcategory)" 
            class="btn-delete"
            :disabled="parseInt(subcategory.element_count) > 0 || parseInt(subcategory.children_count) > 0"
            :title="getDeleteTooltip(subcategory)"
          >
             {{ $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showCreateModal || editingSubcategory" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
         <h3>{{ editingSubcategory ? $t('common.edit') : $t('common.create') }} {{ parentSubcategory ? $t('Подподкатегорию') : $t('Подкатегорию') }}</h3>
        <form @submit.prevent="saveSubcategory">
          <div class="form-group">
             <label>{{ $t('admin.russianNameRequired') }}</label>
            <input v-model="form.name" type="text" required />
          </div>

           <div class="form-group">
             <label>{{ $t('admin.englishNameOptional') }}</label>
             <input v-model="form.nameEn" type="text" />
           </div>

          <div class="form-group">
             <label>{{ $t('legacy.Порядок сортировки') }}</label>
            <input v-model.number="form.sort_order" type="number" />
          </div>

          <div class="form-group">
             <label>{{ $t('Промт-переменная для ИИ') }}</label>
            <textarea
              v-model="form.prompt_variable"
              rows="3"
              placeholder="Описание объектов этой подкатегории (если не задано — наследуется от родителя)"
            ></textarea>
            <small class="hint">Пример: «декоративные арки с живыми цветами»</small>
          </div>

          <div class="form-group">
             <label>{{ $t('Обложка') }} (SVG, JPG, JPEG, PNG)</label>
            <div v-if="coverPreview || (editingSubcategory && editingSubcategory.cover_image)" class="cover-preview">
               <img :src="coverPreview || editingSubcategory.cover_image" :alt="$t('Обложка')" class="preview-image" />
               <button type="button" @click="removeCover" class="btn-remove-cover">{{ $t('Удалить обложку') }}</button>
            </div>
            <input 
              type="file" 
              accept=".svg,.jpg,.jpeg,.png,image/svg+xml,image/jpeg,image/png" 
              @change="handleCoverChange" 
              ref="coverInput"
            />
          </div>

          <div class="form-actions">
             <button type="button" @click="closeModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
             <button type="submit" class="btn-primary">{{ $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '@/api/admin';
import { invalidateCategoryCaches } from '@/utils/cache';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminSubcategories',
  data() {
    return {
      toast: null,
      categoryId: null,
      categoryName: '',
      subcategories: [],
      parentSubcategory: null,
      showCreateModal: false,
      editingSubcategory: null,
      loading: false,
      form: {
        name: '',
        nameEn: '',
        sort_order: 0,
        prompt_variable: ''
      },
      coverFile: null,
      coverPreview: null,
      removeCoverFlag: false
    }
  },
  async created() {
    this.toast = useToast();
    this.categoryId = this.$route.params.categoryId;
    await this.loadSubcategories();
    await this.loadCategoryName();
  },
  methods: {
    openCreateModal() {
      this.editingSubcategory = null;
      this.form = {
        name: '',
        nameEn: '',
        sort_order: 0,
        prompt_variable: ''
      };
      this.coverFile = null;
      this.coverPreview = null;
      this.removeCoverFlag = false;
      this.showCreateModal = true;
    },

    async loadSubcategories() {
      this.loading = true;
      try {
        let response;
        if (this.parentSubcategory) {
          response = await adminApi.getChildSubcategories(this.parentSubcategory.id);
        } else {
          response = await adminApi.getSubcategories(this.categoryId);
        }
        if (response.data.success) {
          this.subcategories = (response.data.subcategories || []).map(sub => ({
            ...sub,
            children_count: parseInt(sub.children_count) || 0,
            element_count: parseInt(sub.element_count) || 0
          }));
        }
      } catch (error) {
         this.toast.error(this.$t('legacy.Ошибка загрузки подкатегорий'));
        console.error('Load subcategories error:', error);
      } finally {
        this.loading = false;
      }
    },

    parseTranslations(item) {
      const translations = item?.translations;
      if (!translations) return {};
      if (typeof translations === 'object' && !Array.isArray(translations)) {
        return translations;
      }
      if (typeof translations === 'string') {
        try {
          const parsed = JSON.parse(translations);
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (error) {
          return {};
        }
      }
      return {};
    },

    getEnglishName(item) {
      const englishName = this.parseTranslations(item).en;
      return typeof englishName === 'string' ? englishName.trim() : '';
    },

    async refreshCategoryCatalog() {
      invalidateCategoryCaches();
      await this.$store.dispatch('category/fetchCategories', { forceRefresh: true });
    },

    async loadCategoryName() {
      try {
        const response = await adminApi.getCategories();
        if (response.data.success && response.data.categories) {
          const category = response.data.categories.find(c => c.id == this.categoryId);
           this.categoryName = category ? category.name : this.$t('legacy.Подкатегории');
        }
      } catch (error) {
        console.error('Load category name error:', error);
         this.categoryName = this.$t('legacy.Подкатегории');
      }
    },

    canAddElementsToSubcategory(subcategory) {
      return parseInt(subcategory.children_count) === 0;
    },

    getDeleteTooltip(subcategory) {
      if (parseInt(subcategory.children_count) > 0) {
         return this.$t('legacy.Сначала удалите дочерние подкатегории');
      }
      if (parseInt(subcategory.element_count) > 0) {
         return this.$t('legacy.Сначала удалите элементы');
      }
      return '';
    },

    openChildren(subcategory) {
      this.parentSubcategory = subcategory;
      this.loadSubcategories();
    },

    backToParent() {
      this.parentSubcategory = null;
      this.loadSubcategories();
    },

    editSubcategory(subcategory) {
      this.editingSubcategory = subcategory;
      this.form = {
        name: subcategory.name,
        nameEn: this.getEnglishName(subcategory),
        sort_order: subcategory.sort_order,
        prompt_variable: subcategory.prompt_variable || ''
      };
      this.coverFile = null;
      this.coverPreview = null;
      this.removeCoverFlag = false;
    },

    handleCoverChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.coverFile = file;
        this.coverPreview = URL.createObjectURL(file);
        this.removeCoverFlag = false;
      }
    },

    removeCover() {
      this.coverFile = null;
      this.coverPreview = null;
      this.removeCoverFlag = true;
      if (this.$refs.coverInput) {
        this.$refs.coverInput.value = '';
      }
    },

    async saveSubcategory() {
      try {
        const formData = new FormData();
        formData.append('name', this.form.name.trim());
        const translations = { ...this.parseTranslations(this.editingSubcategory) };
        const englishName = this.form.nameEn.trim();
        if (englishName) {
          translations.en = englishName;
        } else {
          translations.en = null;
        }
        formData.append('translations', JSON.stringify(translations));
        formData.append('sort_order', this.form.sort_order);
        formData.append('prompt_variable', this.form.prompt_variable || '');
        
        if (this.coverFile) {
          formData.append('cover', this.coverFile);
        }
        
        if (this.editingSubcategory) {
          if (this.removeCoverFlag) {
            formData.append('remove_cover', 'true');
          }
          await adminApi.updateSubcategory(this.editingSubcategory.id, formData);
           this.toast.success(this.$t('legacy.Подкатегория обновлена'));
        } else {
          formData.append('category_id', this.categoryId);
          if (this.parentSubcategory) {
            formData.append('parent_id', this.parentSubcategory.id);
          }
          await adminApi.createSubcategory(formData);
           this.toast.success(this.$t('legacy.Подкатегория создана'));
        }

        await this.refreshCategoryCatalog();
        await this.loadSubcategories();
        this.closeModal();
      } catch (error) {
         const message = error.response?.data?.message || this.$t('legacy.Ошибка сохранения подкатегории');
        this.toast.error(message);
        console.error('Save subcategory error:', error);
      }
    },

    async deleteSubcategory(subcategory) {
      if (parseInt(subcategory.children_count) > 0) {
         this.toast.error(this.$t('legacy.Сначала удалите дочерние подкатегории'));
        return;
      }
      if (parseInt(subcategory.element_count) > 0) {
         this.toast.error(this.$t('legacy.Нельзя удалить подкатегорию с элементами'));
        return;
      }

       if (!confirm(this.$t('legacy.dynamic.subcategoryDelete', { name: subcategory.name }))) return;

      try {
        await adminApi.deleteSubcategory(subcategory.id);
         this.toast.success(this.$t('legacy.Подкатегория удалена'));
        await this.refreshCategoryCatalog();
        await this.loadSubcategories();
      } catch (error) {
         const message = error.response?.data?.message || this.$t('legacy.Ошибка удаления подкатегории');
        this.toast.error(message);
        console.error('Delete subcategory error:', error);
      }
    },

    closeModal() {
      this.showCreateModal = false;
      this.editingSubcategory = null;
       this.form = { name: '', nameEn: '', sort_order: 0, prompt_variable: '' };
      this.coverFile = null;
      this.coverPreview = null;
      this.removeCoverFlag = false;
      if (this.$refs.coverInput) {
        this.$refs.coverInput.value = '';
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/global.scss";
@import "~@/assets/scss/global.scss";

.admin-subcategories {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breadcrumb-link {
  color: #007bff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.breadcrumb-separator {
  color: #666;
}

.breadcrumb-current {
  font-weight: 600;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #666;
}

.subcategories-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #666;
}

.subcategory-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.subcategory-cover {
  flex-shrink: 0;
  
  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
    border-radius: 4px;
    background: #f5f5f5;
    border: 1px solid #ddd;
  }
}

.subcategory-info {
  flex: 1;
  
  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
  }

  p {
    margin: 4px 0;
    font-size: 14px;
    color: #666;
  }

  .children-info {
    color: #007bff;
    font-weight: 500;
  }

  .can-add-elements {
    color: #28a745;
    font-size: 12px;
  }

  .cannot-add-elements {
    color: #dc3545;
    font-size: 12px;
  }
}

.subcategory-actions {
  display: flex;
  gap: 8px;
}

.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }
}

.btn-edit {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #545b62;
  }
}

.btn-children {
  background: #17a2b8;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #138496;
  }
}

.btn-delete {
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #c82333;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.btn-cancel {
  background: #e0e0e0;
  color: #333;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;

  h3 {
    margin: 0 0 20px 0;
  }
}

.form-group {
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
  }

  input, textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
  }

  .hint {
    display: block;
    margin-top: 4px;
    color: #888;
    font-size: 12px;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.cover-preview {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  
  .preview-image {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #f5f5f5;
  }
}

.btn-remove-cover {
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: #c82333;
  }
}
</style>
